import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Seo from '../../components/Seo';
import ProgressContextPanel from '../../components/ui/ProgressContextPanel';
import ChallengeHeader from './components/ChallengeHeader';
import ProofGrid from './components/ProofGrid';
import CompletionCelebration from './components/CompletionCelebration';
import EditProofModal from './components/EditProofModal';
import ProofHeader from '../proof-detail-view/components/ProofHeader';
import ProofImageViewer from '../proof-detail-view/components/ProofImageViewer';
import ProofMetadata from '../proof-detail-view/components/ProofMetadata';
import ProofActions from '../proof-detail-view/components/ProofActions';
import ProofLikeButton from '../proof-detail-view/components/ProofLikeButton';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import CoverImageUpload from '../challenge-creation/components/CoverImageUpload';
import GoalTypeSelector from '../challenge-creation/components/GoalTypeSelector';
import GoalParametersForm from '../challenge-creation/components/GoalParametersForm';
import VisibilitySettings from '../challenge-creation/components/VisibilitySettings';
import AICoverImageWizard from '../project-creation/components/AICoverImageWizard';
import { useAuth } from '../../auth/AuthProvider';
import { supabase } from '../../lib/supabaseClient';
import { getCachedSignedUrl, makeSafeObjectPath, uploadToBucket } from '../../utils/storage';
import { compressImageUnderBytes } from '../../utils/imageCompression';

const ChallengeDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [isOwner, setIsOwner] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationDismissed, setCelebrationDismissed] = useState(false);
  const [editingProof, setEditingProof] = useState(null);
  const [isProgressOpen, setIsProgressOpen] = useState(false);
  const [isProofDetailOpen, setIsProofDetailOpen] = useState(false);
  const [activeProofIndex, setActiveProofIndex] = useState(0);

  const [isChallengeEditOpen, setIsChallengeEditOpen] = useState(false);
  const [challengeEditForm, setChallengeEditForm] = useState({
    title: '',
    description: '',
    coverImage: null,
    coverImageFile: null,
    tagsText: '',
    goalType: '',
    parameters: {},
    settings: {
      defaultPublic: true,
      sequentialUnlock: true,
      celebrationAnimation: false,
    },
  });
  const [challengeEditErrors, setChallengeEditErrors] = useState({});
  const [isSavingChallenge, setIsSavingChallenge] = useState(false);
  const [isDeleteChallengeConfirmOpen, setIsDeleteChallengeConfirmOpen] = useState(false);
  const [isDeletingChallenge, setIsDeletingChallenge] = useState(false);
  const [isAICoverOpen, setIsAICoverOpen] = useState(false);

  const [isReportOpen, setIsReportOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState('spam');
  const [reportDetails, setReportDetails] = useState('');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  const parseTags = (raw) => {
    const items = String(raw || '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    return Array.from(new Set(items));
  };

  const getYoutubeVideoId = (rawUrl) => {
    const url = String(rawUrl || '').trim();
    if (!url) return '';

    const directId = url.match(/^[a-zA-Z0-9_-]{11}$/)?.[0];
    if (directId) return directId;

    try {
      const u = new URL(url);
      const host = (u.hostname || '').replace(/^www\./, '');

      if (host === 'youtu.be') {
        return (u.pathname || '').replace('/', '').slice(0, 11);
      }

      if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com') {
        const v = u.searchParams.get('v');
        if (v) return v.slice(0, 11);

        const m = (u.pathname || '').match(/\/shorts\/([^/?#]+)/) || (u.pathname || '').match(/\/embed\/([^/?#]+)/);
        if (m?.[1]) return String(m[1]).slice(0, 11);
      }

      return '';
    } catch {
      return '';
    }
  };

  const getYoutubeThumb = (rawUrl) => {
    const id = getYoutubeVideoId(rawUrl);
    if (!id) return '';
    return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  };

  const challengeId = location?.state?.challengeId || searchParams?.get('id') || '';

  const [challengeData, setChallengeData] = useState(null);
  const [proofs, setProofs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadFromServer = useCallback(async () => {
    if (!challengeId) return;

    setIsLoading(true);

    const { data: ch, error: chErr } = await supabase
      .from('ch_challenges')
      .select('id, project_id, owner_id, title, description, tags, goal_type, target_count, total_goals, created_at, meta, cover_image_path, cover_image_alt, default_public, sequential_unlock, is_public')
      .eq('id', challengeId)
      .maybeSingle();

    if (chErr || !ch) {
      setChallengeData(null);
      setProofs([]);
      setIsLoading(false);
      return;
    }

    const owner = ch.owner_id;
    const mine = !!user?.id && owner === user.id;
    setIsOwner(mine);

    if (!mine && !ch?.is_public) {
      setChallengeData(null);
      setProofs([]);
      setIsLoading(false);
      return;
    }

    const goalType = ch.goal_type === 'thirty-day' ? 'thirty_day' : ch.goal_type;
    const totalProofs = (() => {
      if (goalType === 'thirty_day') return 30;
      if (ch.total_goals != null) return Number(ch.total_goals || 0);
      if (ch.target_count != null) return Number(ch.target_count || 0);
      return 0;
    })();

    const proofsQuery = supabase
      .from('ch_proofs')
      .select('id, number, title, description, rating, is_public, image_path, youtube_url, submitted_at, started_at, ended_at')
      .eq('challenge_id', ch.id)
      .eq('user_id', owner)
      .order('number', { ascending: true });

    if (!mine) {
      proofsQuery.eq('is_public', true);
    }

    const { data: proofRows, error: pErr } = await proofsQuery;

    if (pErr) {
      setChallengeData(null);
      setProofs([]);
      setIsLoading(false);
      return;
    }

    const proofIds = (proofRows || []).map((r) => r.id).filter(Boolean);
    const { data: likeRows } = proofIds.length > 0
      ? await supabase
        .from('ch_proof_likes')
        .select('proof_id')
        .in('proof_id', proofIds)
      : { data: [] };

    const likeCountByProofId = new Map();
    (likeRows || []).forEach((lk) => {
      likeCountByProofId.set(lk.proof_id, (likeCountByProofId.get(lk.proof_id) || 0) + 1);
    });

    const proofMap = new Map((proofRows || []).map((r) => [r.number, r]));
    const completedProofs = (proofRows || []).length;
    const totalStars = (proofRows || []).reduce((sum, r) => sum + Number(r.rating || 0), 0);
    const averageRating = completedProofs > 0 ? totalStars / completedProofs : 0;

    const coverUrl = ch.cover_image_path
      ? await getCachedSignedUrl({ bucket: 'challenge-covers', path: ch.cover_image_path })
      : '';

    const tiles = await Promise.all(
      Array.from({ length: totalProofs }, (_, i) => i + 1).map(async (n) => {
        const r = proofMap.get(n);
        if (!r) {
          return {
            id: `placeholder-${n}`,
            number: n,
            completed: false,
            image: '',
            imageAlt: '',
            imagePath: '',
            youtubeUrl: '',
            title: '',
            description: '',
            rating: 0,
            likeCount: 0,
            date: '',
            isPublic: mine,
          };
        }

        const thumb = r.youtube_url ? getYoutubeThumb(r.youtube_url) : '';
        const url = r.image_path
          ? await getCachedSignedUrl({ bucket: 'proof-images', path: r.image_path })
          : (thumb || '');
        return {
          id: r.id,
          number: r.number,
          completed: true,
          image: url,
          imageAlt: r.title ? `Proof image for ${r.title}` : '',
          imagePath: r.image_path || '',
          youtubeUrl: r.youtube_url || '',
          title: r.title || '',
          description: r.description || '',
          rating: Number(r.rating || 0),
          likeCount: likeCountByProofId.get(r.id) || 0,
          date: r.submitted_at ? String(r.submitted_at).slice(0, 10) : '',
          isPublic: !!r.is_public,
          startedAt: r.started_at || '',
          endedAt: r.ended_at || '',
        };
      })
    );

    if (mine && user?.id) {
      const syncPayload = await Promise.all(
        (proofRows || [])
          .filter((proof) => proof?.id)
          .map(async (proof) => {
            const baseStart = proof.started_at || proof.submitted_at;
            if (!baseStart) return null;
            const startDate = new Date(baseStart);
            if (Number.isNaN(startDate.getTime())) return null;
            const endDate = proof.ended_at
              ? new Date(proof.ended_at)
              : new Date(startDate.getTime() + 30 * 60 * 1000);
            const iconUrl = proof.youtube_url
              ? getYoutubeThumb(proof.youtube_url)
              : (proof.image_path
                ? await getCachedSignedUrl({ bucket: 'proof-images', path: proof.image_path })
                : '');
            return {
              user_id: user.id,
              title: proof.title || ch.title || '챌린지 인증',
              note: null,
              started_at: startDate.toISOString(),
              ended_at: endDate.toISOString(),
              is_public: !!proof.is_public,
              source_type: 'import_proof',
              source_proof_id: proof.id,
              icon_type: 'preset',
              icon_name: iconUrl ? null : 'Timer',
              icon_path: null,
              icon_url: iconUrl || null,
            };
          })
      );
      const nextPayload = syncPayload.filter(Boolean);
      if (nextPayload.length > 0) {
        await supabase
          .from('dt_time_entries')
          .delete()
          .eq('source_type', 'import_proof')
          .in('source_proof_id', nextPayload.map((p) => p.source_proof_id))
          .eq('user_id', user.id);
        await supabase
          .from('dt_time_entries')
          .insert(nextPayload);
      }
    }

    setChallengeData({
      id: ch.id,
      projectId: ch.project_id,
      title: ch.title,
      description: ch.description,
      tags: Array.isArray(ch.tags) ? ch.tags : [],
      goalType,
      goalParameters: (ch?.meta && typeof ch.meta === 'object' ? (ch.meta?.parameters || {}) : {}),
      settings: {
        defaultPublic: ch?.default_public != null ? !!ch.default_public : !!(ch?.meta?.settings?.defaultPublic ?? true),
        sequentialUnlock: ch?.sequential_unlock != null ? !!ch.sequential_unlock : !!(ch?.meta?.settings?.sequentialUnlock ?? true),
        celebrationAnimation: !!(ch?.meta?.settings?.celebrationAnimation ?? false),
      },
      startDate: ch.created_at ? String(ch.created_at).slice(0, 10) : '',
      endDate: '',
      totalProofs,
      completedProofs,
      totalStars,
      averageRating,
      currentStreak: 0,
      coverImage: coverUrl,
      coverImageAlt: ch.cover_image_alt || ch.title,
      coverImagePath: ch.cover_image_path || '',
    });
    setProofs(tiles);
    setIsLoading(false);
  }, [challengeId, user?.id]);

  const chunk = (arr, size) => {
    const out = [];
    for (let i = 0; i < (arr?.length || 0); i += size) out.push(arr.slice(i, i + size));
    return out;
  };

  useEffect(() => {
    loadFromServer();
  }, [loadFromServer]);

  useEffect(() => {
    const allCompleted = proofs?.length > 0 && proofs?.every((p) => p?.completed);
    if (!isOwner) return;
    if (!allCompleted) return;
    if (celebrationDismissed) return;

    const t = setTimeout(() => setShowCelebration(true), 300);
    return () => clearTimeout(t);
  }, [proofs, celebrationDismissed, isOwner]);

  useEffect(() => {
    setShowCelebration(false);
    setCelebrationDismissed(false);
  }, [challengeId]);

  const isChallengeCompleted = useMemo(
    () => (proofs?.length > 0 && proofs?.every((p) => p?.completed)),
    [proofs]
  );

  const canEditProofs = isOwner && !isChallengeCompleted;

  const handleDeleteProof = useCallback((proofId) => {
    const run = async () => {
      const target = proofs?.find((p) => p?.id === proofId);
      if (!target?.id || String(target.id).startsWith('placeholder-')) return;

      const { error: deleteDestimeError } = await supabase
        .from('dt_time_entries')
        .delete()
        .eq('source_type', 'import_proof')
        .or(`source_proof_id.eq.${target.id},source_id.eq.${target.id}`)
        .eq('user_id', user?.id);

      if (deleteDestimeError) {
        console.error('[challenge-detail] failed to delete destime entry for proof', deleteDestimeError);
      }

      const { error: deleteProofError } = await supabase
        .from('ch_proofs')
        .delete()
        .eq('id', target.id)
        .eq('user_id', user?.id);

      if (deleteProofError) {
        console.error('[challenge-detail] failed to delete proof', deleteProofError);
      }

      if (target?.imagePath) {
        await supabase.storage.from('proof-images').remove([target.imagePath]);
      }

      await loadFromServer();
    };

    run();
  }, [loadFromServer, proofs, user?.id]);

  const handleEditProof = useCallback((proof) => {
    setEditingProof(proof);
  }, []);

  const handleSaveProof = useCallback((updatedProof) => {
    const run = async () => {
      if (!user?.id || !challengeData?.id) return;
      if (!updatedProof?.number) return;

      const isEdit = !!updatedProof?.id && !String(updatedProof.id).startsWith('placeholder-');
      const file = updatedProof?.selectedImage?.file;
      const sourceType = updatedProof?.sourceType || 'image';
      const nextYoutubeUrl = sourceType === 'youtube' ? String(updatedProof?.youtubeUrl || '').trim() : '';

      const previousImagePath = isEdit
        ? (proofs?.find((p) => p?.id === updatedProof?.id)?.imagePath || '')
        : '';

      let imagePath = '';
      if (isEdit) {
        const current = proofs?.find((p) => p?.id === updatedProof?.id);
        imagePath = current?.imagePath || '';
      }

      const previousYoutubeUrl = isEdit
        ? (proofs?.find((p) => p?.id === updatedProof?.id)?.youtubeUrl || '')
        : '';

      const shouldRemoveImage = isEdit && sourceType === 'image' && !updatedProof?.selectedImage;
      if (shouldRemoveImage) {
        imagePath = '';
      }

      if (file) {
        let blob = file;
        if (file.size > 1024 * 1024) {
          blob = await compressImageUnderBytes(file, 1024 * 1024);
        }

        const path = makeSafeObjectPath({
          userId: user.id,
          originalName: file?.name,
          prefix: 'proofs',
        });

        const { error: uploadError } = await uploadToBucket({
          bucket: 'proof-images',
          path,
          blob,
          contentType: blob?.type || 'image/jpeg',
          cacheControl: '31536000',
        });

        if (uploadError) {
          return;
        }

        imagePath = path;
      }

      const nextProofUpdate = {
        title: updatedProof?.title?.trim(),
        description: updatedProof?.description?.trim(),
        rating: updatedProof?.rating,
        is_public: !!updatedProof?.isPublic,
        started_at: updatedProof?.startedAt || null,
        ended_at: updatedProof?.endedAt || null,
      };

      if (sourceType === 'youtube') {
        nextProofUpdate.youtube_url = nextYoutubeUrl || null;
        nextProofUpdate.image_path = null;
      } else {
        nextProofUpdate.youtube_url = null;
        nextProofUpdate.image_path = imagePath || null;
      }

      let savedProofId = updatedProof.id;
      if (isEdit) {
        await supabase
          .from('ch_proofs')
          .update(nextProofUpdate)
          .eq('id', updatedProof.id)
          .eq('user_id', user.id);

        if (previousImagePath && previousImagePath !== imagePath) {
          await supabase.storage.from('proof-images').remove([previousImagePath]);
        }
      } else {
        const { data: insertedProof } = await supabase
          .from('ch_proofs')
          .insert({
            challenge_id: challengeData.id,
            user_id: user.id,
            number: updatedProof.number,
            title: nextProofUpdate.title,
            description: nextProofUpdate.description,
            rating: nextProofUpdate.rating,
            is_public: nextProofUpdate.is_public,
            image_path: nextProofUpdate.image_path,
            youtube_url: nextProofUpdate.youtube_url,
            started_at: nextProofUpdate.started_at,
            ended_at: nextProofUpdate.ended_at,
          })
          .select('id')
          .maybeSingle();
        savedProofId = insertedProof?.id || savedProofId;
      }

      if (sourceType === 'youtube' && previousImagePath) {
        await supabase.storage.from('proof-images').remove([previousImagePath]);
      }

      if (sourceType === 'image' && previousYoutubeUrl) {
        // no-op
      }

      if (savedProofId && updatedProof?.startedAt && updatedProof?.endedAt) {
        const iconUrl = (() => {
          if (sourceType === 'youtube') return getYoutubeThumb(nextYoutubeUrl);
          if (imagePath) return getCachedSignedUrl({ bucket: 'proof-images', path: imagePath });
          return '';
        })();
        const resolvedIconUrl = await Promise.resolve(iconUrl);
        await supabase
          .from('dt_time_entries')
          .delete()
          .eq('source_type', 'import_proof')
          .or(`source_proof_id.eq.${savedProofId},source_id.eq.${savedProofId}`)
          .eq('user_id', user.id);
        await supabase
          .from('dt_time_entries')
          .insert({
            user_id: user.id,
            title: nextProofUpdate.title || challengeData?.title || '챌린지 인증',
            note: null,
            started_at: updatedProof.startedAt,
            ended_at: updatedProof.endedAt,
            is_public: nextProofUpdate.is_public,
            source_type: 'import_proof',
            source_proof_id: savedProofId,
            icon_type: 'preset',
            icon_name: resolvedIconUrl ? null : 'Timer',
            icon_path: null,
            icon_url: resolvedIconUrl || null,
          });
      }

      await loadFromServer();
    };

    run();
  }, [challengeData?.id, loadFromServer, proofs, user?.id]);

  const openProofDetailById = useCallback(
    (proofId) => {
      const idx = proofs?.findIndex((p) => p?.id === proofId);
      if (idx < 0) return;
      setActiveProofIndex(idx);
      setIsProofDetailOpen(true);
    },
    [proofs]
  );

  const openProofUploadByNumber = useCallback(
    (proofNumber) => {
      if (!canEditProofs) return;
      setEditingProof({
        id: null,
        number: proofNumber,
        title: '',
        description: '',
        rating: 0,
        isPublic: true,
        image: '',
      });
    },
    [canEditProofs]
  );

  const progressData = useMemo(
    () => ({
      challengeName: challengeData?.title,
      currentProgress: challengeData?.completedProofs,
      totalSteps: challengeData?.totalProofs,
      completedProofs: challengeData?.completedProofs,
      starRating: Math.round(challengeData?.averageRating),
      nextUnlock:
        challengeData?.completedProofs < challengeData?.totalProofs
          ? `${challengeData?.completedProofs + 1}번째 증명 업로드 가능`
          : '모든 증명 완료!',
      recentAchievements: [],
    }),
    [
      challengeData?.averageRating,
      challengeData?.completedProofs,
      challengeData?.title,
      challengeData?.totalProofs,
    ]
  );

  const reportReasonOptions = useMemo(
    () => [
      { value: 'spam', label: '스팸/광고' },
      { value: 'abuse', label: '욕설/혐오/괴롭힘' },
      { value: 'illegal', label: '불법/유해 정보' },
      { value: 'copyright', label: '저작권/도용' },
      { value: 'other', label: '기타' },
    ],
    []
  );

  const openReportProof = useCallback((proof) => {
    if (!proof?.id || String(proof.id).startsWith('placeholder-')) return;
    if (!user?.id) {
      alert('신고하려면 로그인이 필요합니다');
      navigate('/login-registration');
      return;
    }

    setReportTarget({
      proofId: proof.id,
      proofNumber: proof.number,
      proofTitle: proof.title || '',
    });
    setReportReason('spam');
    setReportDetails('');
    setIsReportOpen(true);
  }, [navigate, user?.id]);

  const submitReport = useCallback(async () => {
    if (!user?.id) return;
    if (!reportTarget?.proofId) return;
    if (!reportReason) {
      alert('신고 사유를 선택해주세요');
      return;
    }

    setIsSubmittingReport(true);
    try {
      const { error } = await supabase
        .from('ch_reports')
        .insert({
          reporter_id: user.id,
          target_type: 'proof',
          proof_id: reportTarget.proofId,
          reason: reportReason,
          details: reportDetails?.trim() || null,
        });

      if (error) {
        if (error.code === '23505') {
          alert('이미 신고한 콘텐츠입니다.');
        } else {
          alert(error.message || '신고 제출에 실패했습니다.');
        }
        return;
      }

      alert('신고가 접수되었습니다. 감사합니다.');
      setIsReportOpen(false);
    } finally {
      setIsSubmittingReport(false);
    }
  }, [reportDetails, reportReason, reportTarget?.proofId, user?.id]);

  return (
    <div className="min-h-screen bg-background pt-16">
      <Seo 
        title={challengeData?.title ? `${challengeData.title} - 챌린지 상세 | 꿈현.Labs` : '챌린지 상세 - 도전 기록 | 꿈현.Labs'} 
        description={challengeData?.description ? `${challengeData.description} | 꿈현.Labs 챌린지 상세 페이지. 도전 과정을 추적하고 성과를 공유하세요.` : '꿈현.Labs 챌린지 상세 페이지. 도전 과정을 추적하고 성과를 공유하세요.'}
        canonicalPath="/challenge-detail" 
      />
      <PrimaryNavigation />
      <BreadcrumbNavigation />
      <div className="pt-4 pb-20 md:pb-24 lg:pb-8">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-5 lg:px-8">
          <div className="max-w-5xl mx-auto">
            {isLoading ? (
              <div className="bg-card rounded-lg shadow-gaming-md p-6">
                <p className="text-sm text-muted-foreground caption">로딩 중...</p>
              </div>
            ) : challengeData ? (
              <ChallengeHeader
                challenge={challengeData}
                onEditChallenge={isOwner ? async () => {
                  setChallengeEditErrors({});

                  let coverUrl = '';
                  const coverPath = challengeData?.coverImagePath || '';
                  if (coverPath) {
                    coverUrl = await getCachedSignedUrl({ bucket: 'challenge-covers', path: coverPath });
                  }

                  setChallengeEditForm({
                    title: challengeData?.title || '',
                    description: challengeData?.description || '',
                    coverImage: coverUrl || null,
                    coverImageFile: null,
                    tagsText: Array.isArray(challengeData?.tags) ? challengeData.tags.join(', ') : '',
                    goalType: challengeData?.goalType === 'thirty_day' ? 'thirty-day' : (challengeData?.goalType || ''),
                    parameters: (challengeData?.goalParameters && typeof challengeData.goalParameters === 'object') ? challengeData.goalParameters : {},
                    settings: {
                      defaultPublic: !!(challengeData?.settings?.defaultPublic ?? true),
                      sequentialUnlock: !!(challengeData?.settings?.sequentialUnlock ?? true),
                      celebrationAnimation: !!(challengeData?.settings?.celebrationAnimation ?? false),
                    },
                  });
                  setIsChallengeEditOpen(true);
                } : undefined}
              />
            ) : (
              <div className="bg-card rounded-lg shadow-gaming-md p-6">
                <p className="text-sm text-muted-foreground caption">챌린지를 찾을 수 없습니다.</p>
              </div>
            )}

            <div className="mb-6 lg:mb-8">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                  실행 인증
                </h2>
                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm text-muted-foreground caption">
                    총 {challengeData?.totalProofs}개
                  </span>
                </div>
              </div>

              <ProofGrid
                proofs={proofs}
                onDeleteProof={canEditProofs ? handleDeleteProof : undefined}
                onEditProof={canEditProofs ? handleEditProof : undefined}
                onOpenProofDetail={openProofDetailById}
                onOpenProofUpload={canEditProofs ? openProofUploadByNumber : undefined}
                onReportProof={openReportProof}
                canEdit={canEditProofs}
              />
            </div>
          </div>
        </div>
      </div>

      {isOwner && (
        <button
          type="button"
          onClick={() => setIsProgressOpen((v) => !v)}
          className="fixed top-20 right-4 md:right-8 z-contextual flex items-center gap-2 px-3 py-2 bg-card/90 backdrop-blur border border-border rounded-md shadow-gaming-md hover:bg-card transition-gaming"
          aria-label={isProgressOpen ? '진행 상황 닫기' : '진행 상황 열기'}
        >
          <Icon name={isProgressOpen ? 'X' : 'TrendingUp'} size={18} className="text-muted-foreground" />
          <span className="text-xs md:text-sm text-foreground font-medium">
            {isProgressOpen ? '진행 상황 닫기' : '진행 상황'}
          </span>
        </button>
      )}

      {isOwner && (
        <ProgressContextPanel
          challengeData={progressData}
          mode="modal"
          isOpen={isProgressOpen}
          onClose={() => setIsProgressOpen(false)}
        />
      )}
      {isProofDetailOpen && (
        <div
          className="fixed inset-0 z-dialog bg-background/80 flex items-center justify-center p-4"
          onClick={() => setIsProofDetailOpen(false)}
        >
          <div className="w-full max-w-4xl" onClick={(e) => e?.stopPropagation()}>
            <div className="bg-card rounded-lg shadow-gaming-lg p-4 md:p-6 lg:p-8 max-h-[90vh] overflow-y-auto">
              <ProofHeader
                currentProof={proofs?.[activeProofIndex]?.number}
                totalProofs={proofs?.length}
                challengeTitle={challengeData?.title}
                onClose={() => setIsProofDetailOpen(false)}
              />

              <div className="space-y-6 md:space-y-8">
                <div className="p-4 md:p-5 bg-muted/40 rounded-lg border border-border">
                  <div className="text-xs text-muted-foreground caption">제목</div>
                  <div className="mt-1 text-lg md:text-xl font-heading font-bold text-foreground break-words">
                    {proofs?.[activeProofIndex]?.title || '제목 없음'}
                  </div>
                </div>

                {proofs?.[activeProofIndex]?.image ? (
                  <ProofImageViewer
                    imageUrl={proofs?.[activeProofIndex]?.image}
                    imageAlt={proofs?.[activeProofIndex]?.imageAlt}
                    onPrevious={() => setActiveProofIndex((v) => Math.max(0, v - 1))}
                    onNext={() => setActiveProofIndex((v) => Math.min((proofs?.length || 1) - 1, v + 1))}
                    hasPrevious={activeProofIndex > 0}
                    hasNext={activeProofIndex < (proofs?.length || 0) - 1}
                  />
                ) : (
                  <div className="w-full h-[220px] md:h-[280px] bg-muted rounded-lg border border-border flex items-center justify-center p-6">
                    <div className="text-center">
                      <div className="text-sm text-muted-foreground caption">이미지가 없는 실행인증입니다</div>
                      <div className="mt-2 text-base md:text-lg font-heading font-bold text-foreground line-clamp-2">
                        {proofs?.[activeProofIndex]?.title || '제목 없음'}
                      </div>
                    </div>
                  </div>
                )}

                <ProofMetadata
                  submissionDate={proofs?.[activeProofIndex]?.date || ''}
                  starRating={proofs?.[activeProofIndex]?.rating || 0}
                  description={proofs?.[activeProofIndex]?.description || ''}
                  youtubeUrl={proofs?.[activeProofIndex]?.youtubeUrl || ''}
                />

                {proofs?.[activeProofIndex]?.id && !String(proofs?.[activeProofIndex]?.id).startsWith('placeholder-') ? (
                  <div className="flex justify-end">
                    <ProofLikeButton proofId={proofs?.[activeProofIndex]?.id} />
                  </div>
                ) : null}

                {canEditProofs ? (
                  <ProofActions
                    onEdit={() => setEditingProof(proofs?.[activeProofIndex])}
                    onDelete={() => {
                      handleDeleteProof(proofs?.[activeProofIndex]?.id);
                      setIsProofDetailOpen(false);
                    }}
                    onClose={() => setIsProofDetailOpen(false)}
                  />
                ) : (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setIsProofDetailOpen(false)}
                      className="px-4 py-2 rounded-md bg-muted/40 text-foreground hover:bg-muted transition-gaming"
                    >
                      닫기
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <AICoverImageWizard
        open={isAICoverOpen}
        onClose={() => setIsAICoverOpen(false)}
        kind="challenge"
        title={challengeEditForm?.title}
        description={challengeEditForm?.description}
        onApply={(image, file) => {
          setChallengeEditErrors((prev) => ({ ...prev, coverImage: '' }));
          setChallengeEditForm((prev) => ({ ...prev, coverImage: image, coverImageFile: file }));
        }}
      />

      <CompletionCelebration
        isVisible={showCelebration}
        onClose={() => {
          setShowCelebration(false);
          setCelebrationDismissed(true);
        }}
        challengeTitle={challengeData?.title}
        totalStars={challengeData?.totalStars} />

      {isOwner && (
        <EditProofModal
          proof={editingProof}
          proofNumber={editingProof?.number}
          isOpen={!!editingProof}
          onClose={() => setEditingProof(null)}
          onSave={handleSaveProof} />
      )}

      {isChallengeEditOpen && (
        <div className="fixed inset-0 z-[999] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-card rounded-lg border border-border shadow-gaming-lg p-4 md:p-6 max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-start justify-between gap-3 mb-4 flex-shrink-0">
              <div>
                <div className="text-lg md:text-xl font-heading font-semibold text-foreground">챌린지 편집</div>
                <div className="text-xs md:text-sm text-muted-foreground caption mt-1">제목/설명/커버 이미지를 수정하거나 삭제합니다</div>
              </div>
              <button
                type="button"
                onClick={() => setIsChallengeEditOpen(false)}
                className="p-2 rounded-md hover:bg-muted transition-gaming text-muted-foreground"
              >
                닫기
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto pr-1">
              <div>
                <label className="text-sm md:text-base font-heading font-semibold text-foreground block mb-2">
                  챌린지 제목 <span className="text-destructive">*</span>
                </label>
                <input
                  value={challengeEditForm.title}
                  onChange={(e) => setChallengeEditForm((prev) => ({ ...prev, title: e?.target?.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-gaming"
                  placeholder="챌린지 제목"
                />
                {challengeEditErrors?.title && (
                  <p className="text-xs text-destructive mt-2">{challengeEditErrors.title}</p>
                )}
              </div>

              <div>
                <label className="text-sm md:text-base font-heading font-semibold text-foreground block mb-2">
                  챌린지 설명
                </label>
                <textarea
                  value={challengeEditForm.description}
                  onChange={(e) => setChallengeEditForm((prev) => ({ ...prev, description: e?.target?.value }))}
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-gaming resize-none"
                  placeholder="챌린지 설명"
                />
              </div>

              <Input
                type="text"
                label="태그"
                description="쉼표(,)로 구분하여 입력하세요"
                placeholder="예: 운동, 습관, 30일"
                value={challengeEditForm.tagsText}
                onChange={(e) => setChallengeEditForm((prev) => ({ ...prev, tagsText: e?.target?.value }))}
                maxLength={200}
              />

              <CoverImageUpload
                coverImage={challengeEditForm.coverImage}
                headerActions={(
                  <Button
                    variant="outline"
                    iconName="Sparkles"
                    iconPosition="left"
                    onClick={() => setIsAICoverOpen(true)}
                    disabled={isSavingChallenge || isDeletingChallenge}
                  >
                    AI 커버 이미지 생성
                  </Button>
                )}
                onImageChange={(image, file) => {
                  if (!image || !file) {
                    setChallengeEditForm((prev) => ({ ...prev, coverImage: null, coverImageFile: null }));
                    return;
                  }
                  if (!file?.type?.startsWith('image/')) {
                    setChallengeEditErrors((prev) => ({ ...prev, coverImage: '이미지 파일만 업로드할 수 있습니다' }));
                    setChallengeEditForm((prev) => ({ ...prev, coverImage: null, coverImageFile: null }));
                    return;
                  }
                  setChallengeEditErrors((prev) => ({ ...prev, coverImage: '' }));
                  setChallengeEditForm((prev) => ({ ...prev, coverImage: image, coverImageFile: file }));
                }}
                error={challengeEditErrors?.coverImage}
              />

              <div className="border-t border-border pt-4">
                <div className="text-sm md:text-base font-heading font-semibold text-foreground mb-2">목표 설정</div>
                <div className="text-xs md:text-sm text-muted-foreground caption mb-4">챌린지 목표 유형과 세부 사항을 수정하세요</div>

                <GoalTypeSelector
                  selectedType={challengeEditForm?.goalType}
                  onTypeChange={(type) => setChallengeEditForm((prev) => ({ ...prev, goalType: type, parameters: {} }))}
                />

                {challengeEditErrors?.goalType ? (
                  <div className="flex items-start space-x-2 p-3 bg-destructive/10 rounded-md border border-destructive/20 mt-4">
                    <Icon name="AlertCircle" size={16} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-destructive">{challengeEditErrors.goalType}</p>
                  </div>
                ) : null}

                {challengeEditForm?.goalType ? (
                  <div className="mt-4">
                    <GoalParametersForm
                      goalType={challengeEditForm?.goalType}
                      parameters={challengeEditForm?.parameters}
                      onParametersChange={(params) => setChallengeEditForm((prev) => ({ ...prev, parameters: params }))}
                      errors={challengeEditErrors}
                    />
                  </div>
                ) : null}
              </div>

              <div className="border-t border-border pt-4">
                <div className="text-sm md:text-base font-heading font-semibold text-foreground mb-2">추가 설정</div>
                <div className="text-xs md:text-sm text-muted-foreground caption mb-4">증명 공개 범위와 챌린지 동작을 수정하세요</div>

                <VisibilitySettings
                  settings={challengeEditForm?.settings}
                  onSettingsChange={(settings) => setChallengeEditForm((prev) => ({ ...prev, settings }))}
                />
              </div>

              {challengeEditErrors?.form && (
                <p className="text-xs md:text-sm text-destructive">{challengeEditErrors.form}</p>
              )}

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setIsChallengeEditOpen(false)} disabled={isSavingChallenge || isDeletingChallenge}>
                  취소
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setIsDeleteChallengeConfirmOpen(true)}
                  disabled={isSavingChallenge || isDeletingChallenge}
                >
                  삭제
                </Button>
                <Button
                  variant="default"
                  iconName="Check"
                  iconPosition="left"
                  loading={isSavingChallenge}
                  onClick={async () => {
                    if (!user?.id || !challengeData?.id) return;
                    const title = challengeEditForm.title?.trim();
                    if (!title) {
                      setChallengeEditErrors((prev) => ({ ...prev, title: '챌린지 제목을 입력하세요' }));
                      return;
                    }

                    const nextErrors = {};
                    if (!challengeEditForm?.goalType) {
                      nextErrors.goalType = '목표 유형을 선택하세요';
                    }

                    const completedCount = Number(challengeData?.completedProofs || 0);
                    const normalizedGoalType = challengeEditForm?.goalType === 'thirty-day' ? 'thirty_day' : challengeEditForm?.goalType;
                    const totalGoals = (() => {
                      if (normalizedGoalType === 'fixed') return Number(challengeEditForm?.parameters?.targetCount || 0);
                      if (normalizedGoalType === 'split') return Number(challengeEditForm?.parameters?.totalGoal || 0);
                      if (normalizedGoalType === 'thirty_day') return 30;
                      return 0;
                    })();

                    if (normalizedGoalType === 'fixed') {
                      if (!challengeEditForm?.parameters?.targetCount || Number(challengeEditForm?.parameters?.targetCount) < 1) {
                        nextErrors.targetCount = '목표 횟수는 1 이상이어야 합니다';
                      }
                    }

                    if (normalizedGoalType === 'split') {
                      if (!challengeEditForm?.parameters?.totalGoal || Number(challengeEditForm?.parameters?.totalGoal) < 1) {
                        nextErrors.totalGoal = '전체 목표 수치를 입력하세요';
                      }
                      if (!challengeEditForm?.parameters?.splitCount || Number(challengeEditForm?.parameters?.splitCount) < 2) {
                        nextErrors.splitCount = '분할 단계는 2 이상이어야 합니다';
                      }
                    }

                    if (normalizedGoalType === 'thirty_day') {
                      if (!challengeEditForm?.parameters?.startDate) {
                        nextErrors.startDate = '시작 날짜를 선택하세요';
                      }
                    }

                    if (totalGoals > 0 && totalGoals < completedCount) {
                      nextErrors.form = `이미 ${completedCount}개의 실행인증을 제출했습니다. 목표 횟수는 ${completedCount} 이상이어야 합니다.`;
                    }

                    if (Object.keys(nextErrors).length > 0) {
                      setChallengeEditErrors((prev) => ({ ...prev, ...nextErrors }));
                      return;
                    }

                    setIsSavingChallenge(true);
                    setChallengeEditErrors((prev) => ({ ...prev, form: '' }));
                    try {
                      let coverImagePath = '';
                      const { data: cur } = await supabase
                        .from('ch_challenges')
                        .select('cover_image_path')
                        .eq('id', challengeData.id)
                        .eq('owner_id', user.id)
                        .maybeSingle();
                      const previousCoverPath = cur?.cover_image_path || '';
                      coverImagePath = previousCoverPath;

                      const shouldRemoveCover = !challengeEditForm?.coverImage && !challengeEditForm?.coverImageFile;
                      if (shouldRemoveCover) {
                        coverImagePath = '';
                      }

                      const file = challengeEditForm.coverImageFile;
                      if (file) {
                        let blob = file;
                        if (file.size > 1024 * 1024) {
                          blob = await compressImageUnderBytes(file, 1024 * 1024);
                        }
                        const path = makeSafeObjectPath({
                          userId: user.id,
                          originalName: file?.name,
                          prefix: 'covers',
                        });
                        const { error: uploadError } = await uploadToBucket({
                          bucket: 'challenge-covers',
                          path,
                          blob,
                          contentType: blob?.type || 'image/jpeg',
                          cacheControl: '31536000',
                        });
                        if (uploadError) {
                          setChallengeEditErrors((prev) => ({ ...prev, form: uploadError?.message || '이미지 업로드에 실패했습니다' }));
                          return;
                        }
                        coverImagePath = path;
                      }

                      const { error: updErr } = await supabase
                        .from('ch_challenges')
                        .update({
                          title,
                          description: challengeEditForm.description?.trim() || null,
                          tags: parseTags(challengeEditForm.tagsText),
                          goal_type: normalizedGoalType,
                          target_count: normalizedGoalType === 'fixed' ? Number(challengeEditForm?.parameters?.targetCount || 0) : null,
                          total_goals: totalGoals,
                          default_public: !!challengeEditForm?.settings?.defaultPublic,
                          sequential_unlock: !!challengeEditForm?.settings?.sequentialUnlock,
                          meta: {
                            parameters: challengeEditForm?.parameters || {},
                            settings: challengeEditForm?.settings || {},
                          },
                          cover_image_path: coverImagePath || null,
                          cover_image_alt: title,
                        })
                        .eq('id', challengeData.id)
                        .eq('owner_id', user.id);

                      if (updErr) {
                        setChallengeEditErrors((prev) => ({ ...prev, form: updErr?.message || '챌린지 수정에 실패했습니다' }));
                        return;
                      }

                      if (previousCoverPath && previousCoverPath !== coverImagePath) {
                        await supabase.storage.from('challenge-covers').remove([previousCoverPath]);
                      }

                      setIsChallengeEditOpen(false);
                      await loadFromServer();
                    } finally {
                      setIsSavingChallenge(false);
                    }
                  }}
                >
                  저장
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isChallengeEditOpen && isDeleteChallengeConfirmOpen && (
        <div className="fixed inset-0 z-[1000] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-card rounded-lg border border-border shadow-gaming-lg p-4 md:p-6">
            <div className="text-lg font-heading font-semibold text-foreground mb-2">챌린지 삭제</div>
            <p className="text-sm text-muted-foreground caption mb-4">
              이 챌린지를 삭제하면 모든 증명(실행인증) 데이터 및 이미지가 함께 삭제됩니다. 이 작업은 되돌릴 수 없습니다.
            </p>
            {challengeEditErrors?.form && (
              <p className="text-xs md:text-sm text-destructive mb-3">{challengeEditErrors.form}</p>
            )}
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" onClick={() => setIsDeleteChallengeConfirmOpen(false)} disabled={isDeletingChallenge}>
                취소
              </Button>
              <Button
                variant="destructive"
                loading={isDeletingChallenge}
                onClick={async () => {
                  if (!user?.id || !challengeData?.id) return;
                  setIsDeletingChallenge(true);
                  setChallengeEditErrors((prev) => ({ ...prev, form: '' }));
                  try {
                    const { data: chRow, error: chErr } = await supabase
                      .from('ch_challenges')
                      .select('id, project_id, owner_id, cover_image_path')
                      .eq('id', challengeData.id)
                      .eq('owner_id', user.id)
                      .maybeSingle();

                    if (chErr || !chRow) {
                      setChallengeEditErrors((prev) => ({ ...prev, form: chErr?.message || '챌린지를 찾을 수 없습니다.' }));
                      return;
                    }

                    const { data: proofRows, error: prErr } = await supabase
                      .from('ch_proofs')
                      .select('id, image_path')
                      .eq('challenge_id', chRow.id)
                      .eq('user_id', user.id);

                    if (prErr) {
                      setChallengeEditErrors((prev) => ({ ...prev, form: prErr?.message || '관련 증명을 불러오지 못했습니다.' }));
                      return;
                    }

                    const coverPaths = chRow.cover_image_path ? [chRow.cover_image_path] : [];
                    const proofImagePaths = (proofRows || []).map((p) => p.image_path).filter(Boolean);

                    for (const batch of chunk(coverPaths, 100)) {
                      await supabase.storage.from('challenge-covers').remove(batch);
                    }
                    for (const batch of chunk(proofImagePaths, 100)) {
                      await supabase.storage.from('proof-images').remove(batch);
                    }

                    const { error: delErr } = await supabase
                      .from('ch_challenges')
                      .delete()
                      .eq('id', chRow.id)
                      .eq('owner_id', user.id);

                    if (delErr) {
                      setChallengeEditErrors((prev) => ({ ...prev, form: delErr?.message || '챌린지 삭제에 실패했습니다.' }));
                      return;
                    }

                    setIsDeleteChallengeConfirmOpen(false);
                    setIsChallengeEditOpen(false);
                    navigate(chRow.project_id ? `/project-detail?id=${chRow.project_id}` : '/project-dashboard');
                  } finally {
                    setIsDeletingChallenge(false);
                  }
                }}
              >
                삭제
              </Button>
            </div>
          </div>
        </div>
      )}

      {isReportOpen && reportTarget && (
        <div
          className="fixed inset-0 z-dialog bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !isSubmittingReport && setIsReportOpen(false)}
        >
          <div
            className="w-full max-w-lg bg-card rounded-lg border border-border shadow-gaming-lg p-4 md:p-6"
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-lg md:text-xl font-heading font-semibold text-foreground">신고하기</div>
                <div className="text-xs md:text-sm text-muted-foreground caption mt-1">
                  #{reportTarget.proofNumber} 증명을 신고합니다.
                </div>
              </div>
              <button
                type="button"
                onClick={() => !isSubmittingReport && setIsReportOpen(false)}
                className="p-2 rounded-md hover:bg-muted transition-gaming text-muted-foreground"
              >
                닫기
              </button>
            </div>

            <div className="space-y-4">
              <Select
                options={reportReasonOptions}
                value={reportReason}
                onChange={setReportReason}
                placeholder="신고 사유 선택"
              />

              <div>
                <label className="text-sm font-medium text-foreground block mb-2">상세 내용(선택)</label>
                <textarea
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e?.target?.value)}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-gaming resize-none"
                  placeholder="추가로 전달할 내용이 있으면 적어주세요."
                  disabled={isSubmittingReport}
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setIsReportOpen(false)} disabled={isSubmittingReport}>
                  취소
                </Button>
                <Button
                  variant="destructive"
                  iconName="Flag"
                  iconPosition="left"
                  onClick={submitReport}
                  loading={isSubmittingReport}
                >
                  신고 제출
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>);

};

export default ChallengeDetail;